//Setting up subcomponents
import { memo } from 'react'
import styled from 'styled-components'
import FormElem from './FormElem'
import GroupFormElem from './GroupFormElem'
import Display from '../display/Display'

import { Form } from '../form/FormComponents'
import SpinnerButton from '../generic/SpinnerButton'
import ModalFormElem from '../cardView/ModalFormElem'
import DocumentPickerFormElem from '../cardView/DocumentPickerFormElem'

import RSwitcher from '../generic/RSwitcher'
import { useCardBusy } from './cardBusy'
import useConditionValues from './useConditionValues'
import { conditionFields, resolveVisibility } from '../../../../common/model/condition/evaluate'

/*
 * The menu sits in a flush-right button row that has no gap of its own, and it
 * is a different kind of control from the buttons beside it. The space is here
 * rather than on Form.ButtonGroup, which draws every button row in the app and
 * whose buttons carry their own margins already.
 */
const MenuSlot = styled.span`
    margin-right: 0.5rem;
`

import useLocale from '../../hooks/useLocale'
import useTranslation from '../../hooks/useTranslation'

const CoreForm = memo(({ section, lineId, expanded = true, validated, dispatch, entity = 'Pop Up', toggleSection = () => { }, useStore, path = [], options, origin = {} }) => {
    const { currentOrgLang } = useLocale()
    const { t } = useTranslation()
    // A card action is running: this section's buttons wait (cardBusy).
    const busy = useCardBusy()
    const sectionName = section?.id || 'Unknown'
    let testId = sectionName + '.'
    lineId !== undefined ? testId = `${testId}${lineId + 1}.` : <></>

    /*
     * A line's own menu, declared as meta.menuItems and rendered at the head of
     * the button row so it sits beside Delete rather than floating over the row.
     *
     * Each item is shown or hidden by a condition read from *this line* --
     * "add a discount" and "remove it" are one slot in two states. The items
     * dispatch like any other button here, so what they do is the line's
     * business, not this component's.
     */
    const menuItems = section?.meta?.menuItems || []
    const menuKeys = [...new Set(menuItems.flatMap(item => conditionFields(item?.condition)))]
    const menuValues = useConditionValues(useStore, menuKeys, lineId, path)
    const menuOptions = menuItems
        .filter(item => resolveVisibility(item, { values: menuValues, context: { mode: options?.mode } }) === 'show')
        .map(item => ({ type: 'item', id: item.action, name: t(item.label), ...item }))


    //console.log('<CoreForm> path: ', path)

    return (
        <>
            {expanded && section?.groups &&
                section?.groups?.map(
                    (item, j) => {
                        return (
                            item.type === 'group'
                                ? <GroupFormElem
                                    section={section}
                                    lang={currentOrgLang}
                                    item={item}
                                    key={`group-${j}`}
                                    lineId={lineId}
                                    validated={validated}
                                    entity={entity}
                                    useStore={useStore}
                                    path={path}
                                    options={options}

                                />
                                : item.type === 'modal'
                                    ? <ModalFormElem
                                        lineId={lineId}
                                        item={item}
                                        key={`group-${j}`}
                                        validated={validated}
                                        section={section}
                                        entity={entity}
                                        useStore={useStore}
                                        path={path}
                                        options={options}
                                        origin={origin}
                                    />
                                    : item.type === 'documentPicker'
                                    ? <DocumentPickerFormElem
                                        item={item}
                                        lineId={lineId}
                                        key={`group-${j}`}
                                        useStore={useStore}
                                        path={path}
                                        options={options}
                                    />
                                    : <FormElem
                                        section={section}
                                        lang={currentOrgLang}
                                        item={item}
                                        lineId={lineId}
                                        entity={entity}
                                        key={`group-${j}`}
                                        validated={validated}
                                        useStore={useStore}
                                        path={path}
                                        options={options}
                                    />
                        )
                    }
                )}
            <Form.ButtonGroup>
                {expanded && menuOptions.length > 0 &&
                    <MenuSlot>
                        <RSwitcher
                            round
                            size='medium'
                            type={testId.slice(0, -1)}
                            currentOption={t(section.meta.menuTitle)}
                            options={menuOptions}
                            handleSelect={(option) => { if (!busy) dispatch(option.action, option) }}
                            disabled={busy}
                        />
                    </MenuSlot>
                }
                {expanded && section?.buttons &&
                    section.buttons.map(
                        (button, k) => {
                            //Note Submit Button sends the form and it is a special case.
                            return (
                                (button.type === 'submit')
                                    ? <SpinnerButton
                                        $shadow
                                        name='submit'
                                        $variant={button.variant}
                                        key={`btn-${k}`}
                                        type={button.type}
                                        id={button.action}
                                        testId={testId + button.action}
                                        disabled={busy}
                                    >
                                        {t(button.label)}
                                    </SpinnerButton>
                                    : (button.size === 'icon')
                                        ? <Form.IconButton
                                            key={`btn-${k}`}
                                            icon={button.icon}
                                            $variant='danger'
                                            $size='2.5rem'
                                            testId={testId + button.action}
                                            clickAction={(e) => {
                                                e.preventDefault()
                                                if (!busy) dispatch(button.action)
                                            }}
                                        />
                                        : <Form.Button
                                            $shadow
                                            $variant={button.variant}
                                            key={`btn-${k}`}
                                            type={button.type}
                                            name={button.action}
                                            disabled={busy}
                                            onClick={(e) => {
                                                e.preventDefault()
                                                dispatch(button.action, button)
                                            }}
                                            data-testid={testId + button.action}
                                        >
                                            {t(button.label)}
                                        </Form.Button>
                            )
                        }
                    )
                }
            </Form.ButtonGroup>
            {
                section?.meta && !section.meta.type === 'lines' && !expanded &&
                <Display
                    type='root'
                    section={section}
                    clickAction={toggleSection}
                />
            }
        </>
    )
})

export default CoreForm
